/**
 * Agent hotel profile — תצוגת מחיר עם hotel_hr_commission_percentage (אותו pipeline כמו תוצאות חיפוש).
 * B2C / ללא agentHotelPricing.enabled נשארים על hotelSellingPrice הרגיל.
 */

import { normalizeHotelSearchCurrencyCode } from "../constants/hotelCurrencies";
import { compactPricingAuditMeta } from "../pricing/compactPricingAuditMeta.js";
import { applyFXSettlement } from "../pricing/pricingEngine.js";
import { isHotelPricingDebug } from "./hotelSearchDebugFlags";
import { computeAgentHotelFlowPriceVm } from "./agentHotelSearchResultsPricing";
import { formatResolvedAgentHotelPriceLine } from "./pricing/agentHotelPricingPipeline";
import { formatHotelIlsRoundedDisplay } from "./formatHotelIlsRoundedDisplay";
import {
  formatHotelProfileHeroLoadedPriceDisplay,
  formatHotelProfilePackagePriceDisplay,
  formatHotelProfileShellSeedPriceDisplay,
  getHotelProfilePackageSupplierUsd,
  pickProfileLoadedHeroTransactionCurrency,
  pickProfileLoadedHeroUsd,
  pickProfileShellSupplierUsd,
  pickProfileShellTransactionCurrency,
  resolveHotelProfilePackageRawCurrency,
  resolveHotelProfileUiDisplayCurrency,
  resolveProfileDisplayPrice,
} from "./hotelSellingPrice";
import {
  pickCheapestHotelProfilePackageDisplay,
  resolveHotelProfilePackageDisplayAmount,
} from "./resolveHotelProfileTopCardDisplayPrice";
import { resolveHotelRoomPackageDisplayPrice } from "./resolveHotelRoomPackageDisplayPrice";
import {
  isAgentHotelCustomerFacingPricingEnabled,
  resolveAgentHotelCustomerFacingPriceIls,
} from "./pricing/resolveAgentHotelCustomerFacingPriceIls";
import { resolveHotelProfileMultiRoomComboBookingSsot } from "./pricing/resolveAgentHotelMultiRoomPricingDisplay";
import { formatCampaignAllotmentPackageDisplay } from "./campaignHotelProfileRooms";

function agentProfilePipelineAuditMeta(profilePriceContext, pricingModel) {
  const base = profilePriceContext?.hotelProfilePricingAuditMeta;
  return compactPricingAuditMeta({
    ...(base && typeof base === "object" ? base : {}),
    pricingModel,
  });
}

function logAgentProfilePrice(component, payload) {
  if (!isHotelPricingDebug()) return;
  try {
    if (typeof console !== "undefined" && console.info) {
      console.info(`[AGENT_HOTEL_PROFILE_PRICE] ${component}`, payload);
    }
  } catch (_) {
    /* ignore */
  }
}

/** בלי entitlement מספרי תקף — אין תצוגת מחיר סוכן (לא raw ולא 0% משוער) */
function missingHotelHrCommissionVm(ap) {
  return {
    basePrice: null,
    baseCurrency: null,
    commissionPercent: null,
    commissionAmount: null,
    finalPrice: null,
    displayCurrency:
      ap?.hotelSearchListDisplayCurrency || ap?.hotelSearchResultsCurrency || null,
    pricingSource: "agent_hotel_flow",
    pricingStatus: "missing_hotel_hr_commission_entitlement",
    reason: "hotel_hr_commission_entitlement_not_loaded",
  };
}

function resolveAgentHotelProfilePriceVm(ap, flowParams, profilePriceContext, pipelinePricingModel) {
  if (!Number.isFinite(ap?.hotelHrCommissionPercentage)) {
    return missingHotelHrCommissionVm(ap);
  }
  return computeAgentHotelFlowPriceVm({
    ...flowParams,
    pricingAuditMeta: agentProfilePipelineAuditMeta(
      profilePriceContext,
      pipelinePricingModel
    ),
  });
}

function resolveAgentProfileDisplayCurrency(profilePriceContext) {
  return (
    resolveHotelProfileUiDisplayCurrency(profilePriceContext) ??
    normalizeHotelSearchCurrencyCode(profilePriceContext?.selectedHotelCurrency)
  );
}

/**
 * שורת מחיר חבילה בפרופיל — סוכן עם HR או לגמרי legacy.
 * @param {object|null|undefined} pkg
 * @param {{ selectedHotelCurrency?: string|null, searchPhrase?: object|null, hotelInfo?: object|null, agentHotelPricing?: { enabled?: boolean, hotelSearchResultsCurrency?: string|null, hotelSearchListDisplayCurrency?: string|null, hotelHrCommissionPercentage?: unknown, fixedHotelCommission?: unknown }|null }|null|undefined} profilePriceContext
 * @returns {string|null}
 */
export function formatHotelProfilePackagePriceLine(pkg, profilePriceContext) {
  const campaignDisplay = formatCampaignAllotmentPackageDisplay(pkg, profilePriceContext);
  if (campaignDisplay?.displayLine) return campaignDisplay.displayLine;
  const ap = profilePriceContext?.agentHotelPricing;
  if (ap?.enabled) {
    const unifiedRow = resolveHotelProfilePackageDisplayAmount(
      pkg,
      profilePriceContext
    );
    if (unifiedRow.displayLine != null) {
      return unifiedRow.displayLine;
    }
  }
  if (profilePriceContext?.dynamicPackageProfileForceIls) {
    const apiAmount = getHotelProfilePackageSupplierUsd(pkg);
    if (apiAmount == null) return null;
    const hotelInfo = profilePriceContext?.hotelInfo ?? null;
    const rawCur = resolveHotelProfilePackageRawCurrency(
      pkg,
      hotelInfo,
      profilePriceContext
    );
    if (rawCur == null || String(rawCur).trim() === "") return null;
    const fx = applyFXSettlement({
      amount: apiAmount,
      fromCurrency: rawCur,
      toCurrency: "ILS",
      usdToIls: profilePriceContext?.usdToIls,
      ilsToUsd: profilePriceContext?.ilsToUsd,
    });
    if (fx.convertedAmount == null) return null;
    if (!ap?.enabled) {
      return formatHotelIlsRoundedDisplay(fx.convertedAmount);
    }
    const resolved = resolveAgentHotelProfilePriceVm(
      ap,
      {
        apiPrice: fx.convertedAmount,
        transactionCurrency: "ILS",
        hotelSearchResultsCurrency: "ILS",
        hotelSearchListDisplayCurrency: "ILS",
        hotelHrCommissionPercentage: ap.hotelHrCommissionPercentage,
        fixedHotelCommission: ap.fixedHotelCommission,
      },
      profilePriceContext,
      "hotel_profile:agent_hr_package_line:dp_force_ils"
    );
    if (resolved.pricingStatus !== "ok") return null;
    return formatResolvedAgentHotelPriceLine(resolved) ?? null;
  }
  if (!ap?.enabled) {
    return formatHotelProfilePackagePriceDisplay(pkg, profilePriceContext);
  }
  const apiAmount = getHotelProfilePackageSupplierUsd(pkg);
  if (apiAmount == null) return null;
  const hotelInfo = profilePriceContext?.hotelInfo ?? null;
  const rawCur = resolveHotelProfilePackageRawCurrency(
    pkg,
    hotelInfo,
    profilePriceContext
  );
  const displayCur = resolveAgentProfileDisplayCurrency(profilePriceContext);
  const core = resolveProfileDisplayPrice({
    rawAmount: apiAmount,
    rawCurrency: rawCur,
    displayCurrency: displayCur,
    usdToIls: profilePriceContext?.usdToIls,
    ilsToUsd: profilePriceContext?.ilsToUsd,
    component: "formatHotelProfilePackagePriceLine",
    hotelId: profilePriceContext?.hotelId ?? null,
    packageId: pkg?.PackageId ?? pkg?.packageID ?? null,
    sourcePath: "package→agentHR",
    profilePriceContext,
  });
  if (core.rawCurrencyMissing || core.fxUnavailable) {
    if (core.formattedAmount == null && apiAmount != null) {
      if (import.meta.env.MODE !== "test") {
        console.log("PROFILE PRICE DEBUG", {
          finalPrice: pkg?.finalPrice,
          rawCurrency: rawCur,
          pricingStatus: core.pricingStatus,
          rawCurrencyMissing: core.rawCurrencyMissing,
          fxUnavailable: core.fxUnavailable,
        });
      }
    }
    return core.formattedAmount;
  }
  const resolved = resolveAgentHotelProfilePriceVm(
    ap,
    {
      apiPrice: core.convertedAmount,
      transactionCurrency: displayCur,
      hotelSearchResultsCurrency: displayCur,
      hotelSearchListDisplayCurrency: displayCur,
      hotelHrCommissionPercentage: ap.hotelHrCommissionPercentage,
      fixedHotelCommission: ap.fixedHotelCommission,
    },
    profilePriceContext,
    "hotel_profile:agent_hr_package_line"
  );
  logAgentProfilePrice("package_row", {
    roomPrice: apiAmount,
    rawCurrency: rawCur,
    displayCurrency: displayCur,
    convertedForAgent: core.convertedAmount,
    conversionApplied: core.conversionApplied,
    ...resolved,
  });
  if (resolved.pricingStatus !== "ok") {
    if (import.meta.env.MODE !== "test") {
      console.log("PROFILE PRICE DEBUG", {
        finalPrice: pkg?.finalPrice,
        rawCurrency: rawCur,
        pricingStatus: resolved.pricingStatus,
        rawCurrencyMissing: core.rawCurrencyMissing,
        fxUnavailable: core.fxUnavailable,
      });
    }
    return null;
  }
  const line = formatResolvedAgentHotelPriceLine(resolved);
  return line ?? null;
}

/**
 * @param {object|null|undefined} pkg
 * @param {object|null|undefined} profilePriceContext
 * @returns {number}
 */
export function getHotelProfilePackageSortValue(pkg, profilePriceContext) {
  const campaignDisplay = formatCampaignAllotmentPackageDisplay(pkg, profilePriceContext);
  if (campaignDisplay?.sortValue != null && Number.isFinite(Number(campaignDisplay.sortValue))) {
    return Number(campaignDisplay.sortValue);
  }
  const ap = profilePriceContext?.agentHotelPricing;
  if (ap?.enabled) {
    const unifiedRow = resolveHotelProfilePackageDisplayAmount(
      pkg,
      profilePriceContext
    );
    if (
      unifiedRow.sortValue != null &&
      Number.isFinite(Number(unifiedRow.sortValue))
    ) {
      return Number(unifiedRow.sortValue);
    }
  }
  const raw = getHotelProfilePackageSupplierUsd(pkg);
  if (raw == null || !Number.isFinite(Number(raw))) return Infinity;
  if (!ap?.enabled) {
    const hotelInfo = profilePriceContext?.hotelInfo ?? null;
    const rawCur = resolveHotelProfilePackageRawCurrency(
      pkg,
      hotelInfo,
      profilePriceContext
    );
    const displayCur = resolveAgentProfileDisplayCurrency(profilePriceContext);
    const core = resolveProfileDisplayPrice({
      rawAmount: raw,
      rawCurrency: rawCur,
      displayCurrency: displayCur,
      usdToIls: profilePriceContext?.usdToIls,
      ilsToUsd: profilePriceContext?.ilsToUsd,
      component: "getHotelProfilePackageSortValue",
      sourcePath: "sort",
      profilePriceContext,
    });
    return core.convertedAmount != null && Number.isFinite(Number(core.convertedAmount))
      ? Number(core.convertedAmount)
      : Infinity;
  }
  const hotelInfo = profilePriceContext?.hotelInfo ?? null;
  const rawCur = resolveHotelProfilePackageRawCurrency(
    pkg,
    hotelInfo,
    profilePriceContext
  );
  const displayCur = resolveAgentProfileDisplayCurrency(profilePriceContext);
  const core = resolveProfileDisplayPrice({
    rawAmount: raw,
    rawCurrency: rawCur,
    displayCurrency: displayCur,
    usdToIls: profilePriceContext?.usdToIls,
    ilsToUsd: profilePriceContext?.ilsToUsd,
    component: "getHotelProfilePackageSortValue",
    sourcePath: "sort→agent",
    profilePriceContext,
  });
  if (core.rawCurrencyMissing || core.fxUnavailable) return Number(core.convertedAmount) || Infinity;
  const resolved = resolveAgentHotelProfilePriceVm(
    ap,
    {
      apiPrice: core.convertedAmount,
      transactionCurrency: displayCur,
      hotelSearchResultsCurrency: displayCur,
      hotelSearchListDisplayCurrency: displayCur,
      hotelHrCommissionPercentage: ap.hotelHrCommissionPercentage,
      fixedHotelCommission: ap.fixedHotelCommission,
    },
    profilePriceContext,
    "hotel_profile:agent_hr_sort"
  );
  if (resolved.pricingStatus !== "ok") return Infinity;
  if (resolved.finalPrice != null) return resolved.finalPrice;
  return Infinity;
}

/**
 * @param {object|null|undefined} hotelInfo
 * @param {object|null|undefined} profilePriceContext
 * @returns {string|null}
 */
export function formatAgentHotelProfileHeroLoadedDisplay(
  hotelInfo,
  profilePriceContext
) {
  const ap = profilePriceContext?.agentHotelPricing;
  if (!ap?.enabled) {
    return formatHotelProfileHeroLoadedPriceDisplay(hotelInfo, profilePriceContext)
      .display;
  }

  const cheapestUnified = pickCheapestHotelProfilePackageDisplay(
    hotelInfo,
    profilePriceContext
  );
  if (cheapestUnified?.displayLine != null) {
    logAgentProfilePrice("hero_loaded_unified_ssot", {
      displayLine: cheapestUnified.displayLine,
      chosenDisplayAmount: cheapestUnified.chosenDisplayAmount,
      chosenDisplayCurrency: cheapestUnified.chosenDisplayCurrency,
      sourcePath: cheapestUnified.sourcePath,
      rawFinalPrice: cheapestUnified.rawFinalPrice,
      rawOurPriceWithCc: cheapestUnified.rawOurPriceWithCc,
      rejectedSourceReason: cheapestUnified.rejectedSourceReason,
      packageId: cheapestUnified.packageId,
    });
    return cheapestUnified.displayLine;
  }

  if (profilePriceContext?.dynamicPackageProfileForceIls) {
    const pickDp = pickProfileLoadedHeroUsd(hotelInfo);
    if (pickDp.supplierUsd == null) return null;
    const rawCurDp = pickProfileLoadedHeroTransactionCurrency(hotelInfo, pickDp);
    if (rawCurDp == null || String(rawCurDp).trim() === "") return null;
    const fxDp = applyFXSettlement({
      amount: pickDp.supplierUsd,
      fromCurrency: rawCurDp,
      toCurrency: "ILS",
      usdToIls: profilePriceContext?.usdToIls,
      ilsToUsd: profilePriceContext?.ilsToUsd,
    });
    if (fxDp.convertedAmount == null) return null;
    const resolvedDp = resolveAgentHotelProfilePriceVm(
      ap,
      {
        apiPrice: fxDp.convertedAmount,
        transactionCurrency: "ILS",
        hotelSearchResultsCurrency: "ILS",
        hotelSearchListDisplayCurrency: "ILS",
        hotelHrCommissionPercentage: ap.hotelHrCommissionPercentage,
        fixedHotelCommission: ap.fixedHotelCommission,
      },
      profilePriceContext,
      "hotel_profile:agent_hr_hero_loaded:dp_force_ils"
    );
    if (resolvedDp.pricingStatus !== "ok") return null;
    return formatResolvedAgentHotelPriceLine(resolvedDp) ?? null;
  }
  const pick = pickProfileLoadedHeroUsd(hotelInfo);
  if (pick.supplierUsd == null) return null;
  const rawCur = pickProfileLoadedHeroTransactionCurrency(hotelInfo, pick);
  const displayCur = resolveAgentProfileDisplayCurrency(profilePriceContext);
  const core = resolveProfileDisplayPrice({
    rawAmount: pick.supplierUsd,
    rawCurrency: rawCur,
    displayCurrency: displayCur,
    usdToIls: profilePriceContext?.usdToIls,
    ilsToUsd: profilePriceContext?.ilsToUsd,
    component: "formatAgentHotelProfileHeroLoadedDisplay",
    hotelId:
      hotelInfo?.HotelId ??
      hotelInfo?.hotelId ??
      profilePriceContext?.hotelId ??
      null,
    sourcePath: pick.priceSource ?? "hero",
    profilePriceContext,
  });
  if (core.rawCurrencyMissing || core.fxUnavailable) {
    return core.formattedAmount;
  }
  const resolved = resolveAgentHotelProfilePriceVm(
    ap,
    {
      apiPrice: core.convertedAmount,
      transactionCurrency: displayCur,
      hotelSearchResultsCurrency: displayCur,
      hotelSearchListDisplayCurrency: displayCur,
      hotelHrCommissionPercentage: ap.hotelHrCommissionPercentage,
      fixedHotelCommission: ap.fixedHotelCommission,
    },
    profilePriceContext,
    "hotel_profile:agent_hr_hero_loaded"
  );
  logAgentProfilePrice("hero_loaded", {
    roomPrice: pick.supplierUsd,
    rawCurrency: rawCur,
    displayCurrency: displayCur,
    convertedForAgent: core.convertedAmount,
    ...resolved,
  });
  if (resolved.pricingStatus !== "ok") return null;
  const line = formatResolvedAgentHotelPriceLine(resolved);
  return line ?? null;
}

/**
 * @param {{ compositePricingMeta: object|null, seed: object|null, profilePriceContext: object|null }} params
 * @returns {string|null}
 */
export function formatAgentHotelProfileShellSeedDisplay({
  compositePricingMeta,
  seed,
  profilePriceContext,
}) {
  const ap = profilePriceContext?.agentHotelPricing;
  if (!ap?.enabled) {
    return formatHotelProfileShellSeedPriceDisplay({
      compositePricingMeta,
      seed,
      profilePriceContext,
    }).display;
  }
  const shellPick = pickProfileShellSupplierUsd({
    compositePricingMeta,
    seed,
  });
  if (shellPick.supplierUsd == null) return null;
  const rawCur = pickProfileShellTransactionCurrency({
    compositePricingMeta,
    seed,
    profilePriceContext,
  });
  const displayCur = resolveAgentProfileDisplayCurrency(profilePriceContext);
  const core = resolveProfileDisplayPrice({
    rawAmount: shellPick.supplierUsd,
    rawCurrency: rawCur,
    displayCurrency: displayCur,
    usdToIls: profilePriceContext?.usdToIls,
    ilsToUsd: profilePriceContext?.ilsToUsd,
    component: "formatAgentHotelProfileShellSeedDisplay",
    hotelId: profilePriceContext?.hotelId ?? seed?.id ?? null,
    sourcePath: shellPick.priceSource ?? "shell",
    profilePriceContext,
  });
  if (core.rawCurrencyMissing || core.fxUnavailable) {
    return core.formattedAmount;
  }
  const resolved = resolveAgentHotelProfilePriceVm(
    ap,
    {
      apiPrice: core.convertedAmount,
      transactionCurrency: displayCur,
      hotelSearchResultsCurrency: displayCur,
      hotelSearchListDisplayCurrency: displayCur,
      hotelHrCommissionPercentage: ap.hotelHrCommissionPercentage,
      fixedHotelCommission: ap.fixedHotelCommission,
    },
    profilePriceContext,
    "hotel_profile:agent_hr_shell_seed"
  );
  if (resolved.pricingStatus !== "ok") return null;
  const line = formatResolvedAgentHotelPriceLine(resolved);
  return line ?? null;
}

/**
 * המרת סכום במטבע תצוגה ל־ILS עגול למעלה — ל־SSOT בסל.
 */
export function bookingDisplayAmountToIlsCeil(amount, currency, profilePriceContext) {
  if (amount == null || !Number.isFinite(Number(amount)) || Number(amount) <= 0) return null;
  const n = Number(amount);
  const cur = String(currency || "").trim().toUpperCase();
  if (!cur || cur === "ILS") return Math.ceil(n);
  const fx = applyFXSettlement({
    amount: n,
    fromCurrency: cur,
    toCurrency: "ILS",
    usdToIls: profilePriceContext?.usdToIls,
    ilsToUsd: profilePriceContext?.ilsToUsd,
  });
  if (fx?.convertedAmount == null || !Number.isFinite(Number(fx.convertedAmount))) return null;
  return Math.ceil(Number(fx.convertedAmount));
}

function logBookingSsotUnifiedTrace(pkg, packageId, resolvedBookingSsotIls, opts) {
  const profileDisplayedIls =
    pkg?.displayCurrencies?.formattedIls ??
    pkg?.displayCurrencies?.ils ??
    pkg?.ourPrice?.priceWithCommissionAndCreditCardFee ??
    null;
  try {
    if (typeof console !== "undefined" && console.info) {
      console.info("[BOOKING_SSOT_UNIFIED_TRACE]", {
        packageId,
        profileDisplayedIls,
        resolvedBookingSsotIls,
        source: opts.source,
        usedDisplayCurrencies: opts.usedDisplayCurrencies,
        usedPwccf: opts.usedPwccf,
        fallbackFxUsed: opts.fallbackFxUsed,
      });
    }
  } catch (_) {
    /* ignore */
  }
}

/**
 * SSOT כמו תצוגת Hotel Profile: displayCurrencies.ils → ourPrice.PWCCF.
 * Agent hotel flow skips this — supplier quote only; customer final via HR resolver.
 */
function tryUnifiedHotelPackageProfileSsotIls(pkg, profilePriceContext = null) {
  if (isAgentHotelCustomerFacingPricingEnabled(profilePriceContext)) {
    return null;
  }
  if (!pkg || typeof pkg !== "object") return null;
  const dc = pkg.displayCurrencies;
  if (dc && dc.ils != null) {
    const n = Number(dc.ils);
    if (Number.isFinite(n) && n > 0) {
      return {
        bookingSsotIls: Math.ceil(n),
        source: "display_currencies.ils",
        usedDisplayCurrencies: true,
        usedPwccf: false,
      };
    }
  }
  const pwccf = pkg.ourPrice?.priceWithCommissionAndCreditCardFee;
  if (pwccf != null) {
    const n = Number(pwccf);
    if (Number.isFinite(n) && n > 0) {
      return {
        bookingSsotIls: Math.ceil(n),
        source: "our_price.priceWithCommissionAndCreditCardFee",
        usedDisplayCurrencies: false,
        usedPwccf: true,
      };
    }
  }
  if (
    profilePriceContext &&
    !profilePriceContext?.agentHotelPricing?.enabled
  ) {
    const roomSsot = resolveHotelRoomPackageDisplayPrice(pkg, profilePriceContext);
    if (roomSsot.amountIls != null && roomSsot.amountIls > 0) {
      return {
        bookingSsotIls: Math.ceil(roomSsot.amountIls),
        source: roomSsot.wasConverted
          ? "room_display_ssot_usd_fx"
          : roomSsot.sourceCurrency ?? "room_display_ssot",
        usedDisplayCurrencies: false,
        usedPwccf: false,
      };
    }
  }
  return null;
}

function wrapBookingSsotResult(pkg, packageId, bookingSsotIls, trace, logOpts, agentOurPrice = null) {
  logBookingSsotUnifiedTrace(pkg, packageId, bookingSsotIls, logOpts);
  return { bookingSsotIls, trace, agentOurPrice };
}

/**
 * SSOT מספרי למחיר ההזמנה — מיושר לתצוגת Hotel Profile (displayCurrencies / PWCCF) לפני FX legacy.
 * @returns {{ bookingSsotIls: number|null, trace: object }}
 */
export function resolveHotelProfilePackageBookingSsot(
  pkg,
  profilePriceContext,
  extra = {}
) {
  const roomId =
    extra.roomId ??
    extra.selectedRoom?.id ??
    extra.selectedRoom?.roomId ??
    extra.selectedRoom?.RoomId ??
    null;
  const packageId = pkg?.PackageId ?? pkg?.packageID ?? pkg?.packageId ?? null;
  const rawSupplier = getHotelProfilePackageSupplierUsd(pkg);
  const hotelInfo = profilePriceContext?.hotelInfo ?? null;
  const ap = profilePriceContext?.agentHotelPricing;

  const baseTrace = {
    packageId,
    roomId,
    rawSupplier,
    rawCurrency: null,
    displayCurrency: null,
    finalInDisplayCurrency: null,
    bookingSsotIls: null,
    pipeline: null,
    pricingStatus: null,
  };

  if (!pkg || !profilePriceContext) {
    const trace = { ...baseTrace, pipeline: "noop", reason: "missing_pkg_or_context" };
    return wrapBookingSsotResult(pkg, packageId, null, trace, {
      source: "noop_missing_pkg_or_context",
      usedDisplayCurrencies: false,
      usedPwccf: false,
      fallbackFxUsed: false,
    });
  }

  const campaignDisplay = formatCampaignAllotmentPackageDisplay(pkg, profilePriceContext);
  if (
    campaignDisplay?.chosenDisplayAmount != null &&
    Number.isFinite(Number(campaignDisplay.chosenDisplayAmount))
  ) {
    const bookingSsotIls = Number(campaignDisplay.chosenDisplayAmount);
    const trace = {
      ...baseTrace,
      rawCurrency: campaignDisplay.contractCurrency ?? "USD",
      displayCurrency: campaignDisplay.chosenDisplayCurrency ?? "ILS",
      finalInDisplayCurrency: bookingSsotIls,
      bookingSsotIls,
      pipeline: "campaign_allotment_net_x_markup_admin_fx",
      pricingStatus: "ok",
      skipHotelHrCommission: true,
      adminFxRate: campaignDisplay.adminFxRate ?? null,
      campaignMarkupFactor: campaignDisplay.markupFactor ?? null,
    };
    return wrapBookingSsotResult(pkg, packageId, bookingSsotIls, trace, {
      source: "campaign_allotment_net_x_markup_admin_fx",
      usedDisplayCurrencies: false,
      usedPwccf: false,
      fallbackFxUsed: false,
    });
  }

  const multiRoomComboSsot = resolveHotelProfileMultiRoomComboBookingSsot({
    room: extra.selectedRoom ?? null,
    pkg,
    profilePriceContext,
    order: extra.order ?? null,
  });
  if (multiRoomComboSsot?.bookingSsotIls != null) {
    const trace = {
      ...baseTrace,
      ...multiRoomComboSsot.trace,
      bookingSsotIls: multiRoomComboSsot.bookingSsotIls,
      finalInDisplayCurrency: multiRoomComboSsot.bookingSsotIls,
      displayCurrency: resolveHotelProfileUiDisplayCurrency(profilePriceContext),
    };
    return wrapBookingSsotResult(
      pkg,
      packageId,
      multiRoomComboSsot.bookingSsotIls,
      trace,
      {
        source: multiRoomComboSsot.trace?.source ?? "hotel_profile_combo_package",
        usedDisplayCurrencies: false,
        usedPwccf: false,
        fallbackFxUsed: false,
      },
      multiRoomComboSsot.agentOurPrice ?? null
    );
  }

  if (isAgentHotelCustomerFacingPricingEnabled(profilePriceContext)) {
    const cf = resolveAgentHotelCustomerFacingPriceIls({
      pkg,
      hotelInfo,
      profilePriceContext,
      pricingModel: "hotel_profile:agent_hr_booking_ssot",
    });
    if (cf.pricingStatus === "ok" && cf.bookingSsotIls != null) {
      const trace = {
        ...baseTrace,
        rawCurrency: cf.rawSupplierCurrency ?? baseTrace.rawCurrency,
        displayCurrency: cf.displayCurrency,
        finalInDisplayCurrency: cf.priceWithCommission,
        bookingSsotIls: cf.bookingSsotIls,
        pipeline: cf.pipeline ?? "agent_hr_customer_facing_ssot",
        pricingStatus: "ok",
        hrCommissionPercent: cf.hrCommissionPercent,
        hrCommission: cf.hrCommission,
        supplierNet: cf.supplierNet,
      };
      logAgentProfilePrice("booking_ssot_agent_hr", {
        roomPrice: rawSupplier,
        ...cf,
      });
      return wrapBookingSsotResult(pkg, packageId, cf.bookingSsotIls, trace, {
        source: "agent_hr_customer_facing_ssot",
        usedDisplayCurrencies: false,
        usedPwccf: false,
        fallbackFxUsed: Boolean(cf.convertedSupplierAmount != null),
      }, cf.ourPrice);
    }
  }

  const unifiedSsot = tryUnifiedHotelPackageProfileSsotIls(pkg, profilePriceContext);
  if (unifiedSsot) {
    const trace = {
      ...baseTrace,
      bookingSsotIls: unifiedSsot.bookingSsotIls,
      pipeline: "unified_profile_ssot",
      pricingStatus: "ok",
    };
    return wrapBookingSsotResult(pkg, packageId, unifiedSsot.bookingSsotIls, trace, {
      source: unifiedSsot.source,
      usedDisplayCurrencies: unifiedSsot.usedDisplayCurrencies,
      usedPwccf: unifiedSsot.usedPwccf,
      fallbackFxUsed: false,
    });
  }

  if (profilePriceContext?.dynamicPackageProfileForceIls) {
    if (rawSupplier == null) {
      const trace = { ...baseTrace, pipeline: "dp_force_ils_no_raw" };
      return wrapBookingSsotResult(pkg, packageId, null, trace, {
        source: "dp_force_ils_no_raw",
        usedDisplayCurrencies: false,
        usedPwccf: false,
        fallbackFxUsed: false,
      });
    }
    const rawCur = resolveHotelProfilePackageRawCurrency(
      pkg,
      hotelInfo,
      profilePriceContext
    );
    baseTrace.rawCurrency = rawCur;
    if (rawCur == null || String(rawCur).trim() === "") {
      const trace = { ...baseTrace, pipeline: "dp_force_ils_no_currency" };
      return wrapBookingSsotResult(pkg, packageId, null, trace, {
        source: "dp_force_ils_no_currency",
        usedDisplayCurrencies: false,
        usedPwccf: false,
        fallbackFxUsed: false,
      });
    }
    const fx = applyFXSettlement({
      amount: rawSupplier,
      fromCurrency: rawCur,
      toCurrency: "ILS",
      usdToIls: profilePriceContext?.usdToIls,
      ilsToUsd: profilePriceContext?.ilsToUsd,
    });
    if (fx.convertedAmount == null) {
      const trace = { ...baseTrace, pipeline: "dp_force_ils_fx_failed" };
      return wrapBookingSsotResult(pkg, packageId, null, trace, {
        source: "dp_force_ils_fx_failed",
        usedDisplayCurrencies: false,
        usedPwccf: false,
        fallbackFxUsed: false,
      });
    }
    if (!ap?.enabled) {
      const ils = bookingDisplayAmountToIlsCeil(fx.convertedAmount, "ILS", profilePriceContext);
      const trace = {
        ...baseTrace,
        displayCurrency: "ILS",
        finalInDisplayCurrency: fx.convertedAmount,
        bookingSsotIls: ils,
        pipeline: "dp_force_ils_b2c",
        pricingStatus: "ok",
      };
      return wrapBookingSsotResult(pkg, packageId, ils, trace, {
        source: "dp_force_ils_b2c",
        usedDisplayCurrencies: false,
        usedPwccf: false,
        fallbackFxUsed: true,
      });
    }
    const resolved = resolveAgentHotelProfilePriceVm(
      ap,
      {
        apiPrice: fx.convertedAmount,
        transactionCurrency: "ILS",
        hotelSearchResultsCurrency: "ILS",
        hotelSearchListDisplayCurrency: "ILS",
        hotelHrCommissionPercentage: ap.hotelHrCommissionPercentage,
        fixedHotelCommission: ap.fixedHotelCommission,
      },
      profilePriceContext,
      "hotel_profile:agent_hr_booking_ssot:dp_force_ils"
    );
    baseTrace.pricingStatus = resolved.pricingStatus;
    if (resolved.pricingStatus !== "ok" || resolved.finalPrice == null) {
      const trace = {
        ...baseTrace,
        displayCurrency: "ILS",
        pipeline: "dp_force_ils_agent_not_ok",
        reason: resolved.reason,
      };
      return wrapBookingSsotResult(pkg, packageId, null, trace, {
        source: "dp_force_ils_agent_not_ok",
        usedDisplayCurrencies: false,
        usedPwccf: false,
        fallbackFxUsed: false,
      });
    }
    const ils = bookingDisplayAmountToIlsCeil(
      resolved.finalPrice,
      "ILS",
      profilePriceContext
    );
    const trace = {
      ...baseTrace,
      displayCurrency: "ILS",
      finalInDisplayCurrency: resolved.finalPrice,
      bookingSsotIls: ils,
      pipeline: "hotel_profile:agent_hr_booking_ssot:dp_force_ils",
      pricingStatus: "ok",
    };
    return wrapBookingSsotResult(pkg, packageId, ils, trace, {
      source: "hotel_profile:agent_hr_booking_ssot:dp_force_ils",
      usedDisplayCurrencies: false,
      usedPwccf: false,
      fallbackFxUsed: true,
    });
  }

  if (!ap?.enabled) {
    if (rawSupplier == null) {
      const trace = { ...baseTrace, pipeline: "b2c_no_raw" };
      return wrapBookingSsotResult(pkg, packageId, null, trace, {
        source: "b2c_no_raw",
        usedDisplayCurrencies: false,
        usedPwccf: false,
        fallbackFxUsed: false,
      });
    }
    const rawCur = resolveHotelProfilePackageRawCurrency(
      pkg,
      hotelInfo,
      profilePriceContext
    );
    const disp = resolveHotelProfileUiDisplayCurrency(profilePriceContext);
    baseTrace.rawCurrency = rawCur;
    baseTrace.displayCurrency = disp;
    const core = resolveProfileDisplayPrice({
      rawAmount: rawSupplier,
      rawCurrency: rawCur,
      displayCurrency: disp,
      usdToIls: profilePriceContext?.usdToIls,
      ilsToUsd: profilePriceContext?.ilsToUsd,
      component: "resolveHotelProfilePackageBookingSsot",
      hotelId: profilePriceContext?.hotelId ?? null,
      packageId,
      sourcePath: "package→b2c_ssot",
      profilePriceContext,
    });
    if (core.convertedAmount == null) {
      const trace = {
        ...baseTrace,
        pipeline: "b2c_no_converted",
        pricingStatus: core.pricingStatus,
        rawCurrencyMissing: core.rawCurrencyMissing,
        fxUnavailable: core.fxUnavailable,
      };
      return wrapBookingSsotResult(pkg, packageId, null, trace, {
        source: "b2c_no_converted",
        usedDisplayCurrencies: false,
        usedPwccf: false,
        fallbackFxUsed: false,
      });
    }
    const ils = bookingDisplayAmountToIlsCeil(core.convertedAmount, disp, profilePriceContext);
    const trace = {
      ...baseTrace,
      finalInDisplayCurrency: core.convertedAmount,
      bookingSsotIls: ils,
      pipeline: "b2c_resolveProfileDisplayPrice",
      pricingStatus: core.pricingStatus || "ok",
    };
    return wrapBookingSsotResult(pkg, packageId, ils, trace, {
      source: "legacy_b2c_resolveProfileDisplayPrice",
      usedDisplayCurrencies: false,
      usedPwccf: false,
      fallbackFxUsed: true,
    });
  }

  if (rawSupplier == null) {
    const trace = { ...baseTrace, pipeline: "agent_no_raw" };
    return wrapBookingSsotResult(pkg, packageId, null, trace, {
      source: "agent_no_raw",
      usedDisplayCurrencies: false,
      usedPwccf: false,
      fallbackFxUsed: false,
    });
  }
  const rawCur = resolveHotelProfilePackageRawCurrency(
    pkg,
    hotelInfo,
    profilePriceContext
  );
  const displayCur = resolveAgentProfileDisplayCurrency(profilePriceContext);
  baseTrace.rawCurrency = rawCur;
  baseTrace.displayCurrency = displayCur;
  const core = resolveProfileDisplayPrice({
    rawAmount: rawSupplier,
    rawCurrency: rawCur,
    displayCurrency: displayCur,
    usdToIls: profilePriceContext?.usdToIls,
    ilsToUsd: profilePriceContext?.ilsToUsd,
    component: "resolveHotelProfilePackageBookingSsot",
    hotelId: profilePriceContext?.hotelId ?? null,
    packageId,
    sourcePath: "package→agentHR_ssot",
    profilePriceContext,
  });
  if (core.rawCurrencyMissing || core.fxUnavailable) {
    const ilsFallback =
      core.convertedAmount != null
        ? bookingDisplayAmountToIlsCeil(core.convertedAmount, displayCur, profilePriceContext)
        : null;
    const trace = {
      ...baseTrace,
      finalInDisplayCurrency: core.convertedAmount,
      bookingSsotIls: ilsFallback,
      pipeline: "agent_pre_vm_currency_issue",
      rawCurrencyMissing: core.rawCurrencyMissing,
      fxUnavailable: core.fxUnavailable,
      pricingStatus: core.pricingStatus,
    };
    return wrapBookingSsotResult(pkg, packageId, ilsFallback, trace, {
      source: "agent_pre_vm_currency_issue",
      usedDisplayCurrencies: false,
      usedPwccf: false,
      fallbackFxUsed: ilsFallback != null,
    });
  }
  const resolved = resolveAgentHotelProfilePriceVm(
    ap,
    {
      apiPrice: core.convertedAmount,
      transactionCurrency: displayCur,
      hotelSearchResultsCurrency: displayCur,
      hotelSearchListDisplayCurrency: displayCur,
      hotelHrCommissionPercentage: ap.hotelHrCommissionPercentage,
      fixedHotelCommission: ap.fixedHotelCommission,
    },
    profilePriceContext,
    "hotel_profile:agent_hr_booking_ssot"
  );
  baseTrace.pricingStatus = resolved.pricingStatus;
  if (resolved.pricingStatus !== "ok" || resolved.finalPrice == null) {
    const trace = {
      ...baseTrace,
      finalInDisplayCurrency: core.convertedAmount,
      pipeline: "agent_vm_not_ok",
      reason: resolved.reason,
    };
    return wrapBookingSsotResult(pkg, packageId, null, trace, {
      source: "agent_vm_not_ok",
      usedDisplayCurrencies: false,
      usedPwccf: false,
      fallbackFxUsed: false,
    });
  }
  const ils = bookingDisplayAmountToIlsCeil(
    resolved.finalPrice,
    displayCur,
    profilePriceContext
  );
  logAgentProfilePrice("booking_ssot", {
    roomPrice: rawSupplier,
    rawCurrency: rawCur,
    displayCurrency: displayCur,
    convertedForAgent: core.convertedAmount,
    bookingSsotIls: ils,
    ...resolved,
  });
  const trace = {
    ...baseTrace,
    finalInDisplayCurrency: resolved.finalPrice,
    bookingSsotIls: ils,
    pipeline: "hotel_profile:agent_hr_package_line_parity",
    pricingStatus: "ok",
  };
  return wrapBookingSsotResult(pkg, packageId, ils, trace, {
    source: "legacy_agent_hr_resolveProfileDisplayPrice",
    usedDisplayCurrencies: false,
    usedPwccf: false,
    fallbackFxUsed: true,
  });
}
